import { toPng } from 'html-to-image';

/**
 * A slide is 16:9, so the export is too — always, whatever the window is doing.
 * Capturing the viewport as it happens to be sized gives you a picture that has
 * to be cropped or letterboxed by hand before it can go in a deck.
 */
export const EXPORT_WIDTH = 1920;
export const EXPORT_HEIGHT = 1080;

/** Breathing room around the content, as a fraction of the frame. */
const PADDING = 0.06;

export interface Box {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface CaptureOptions {
  /** Painted behind the content, since the page background is not captured. */
  background: string;
  width?: number;
  height?: number;
  /** Cap on enlargement: a two-card chart blown up to 1080p looks broken. */
  maxScale?: number;
}

/**
 * The transform that fits a box of content into the frame: centred, uniformly
 * scaled, never enlarged past `maxScale`.
 */
export function fitTransform(
  box: Box,
  options: { width: number; height: number; maxScale: number },
): { x: number; y: number; scale: number } {
  const contentWidth = Math.max(1, box.maxX - box.minX);
  const contentHeight = Math.max(1, box.maxY - box.minY);
  const usableWidth = options.width * (1 - PADDING * 2);
  const usableHeight = options.height * (1 - PADDING * 2);

  const scale = Math.min(usableWidth / contentWidth, usableHeight / contentHeight, options.maxScale);

  return {
    x: (options.width - contentWidth * scale) / 2 - box.minX * scale,
    y: (options.height - contentHeight * scale) / 2 - box.minY * scale,
    scale,
  };
}

/**
 * Render an element into a fixed-size PNG.
 *
 * The element is captured at its own size and then transformed into the frame,
 * rather than the window being resized: the chart keeps its layout, the fonts
 * keep their metrics, and nothing on screen moves while it happens.
 */
export async function capturePng(element: HTMLElement, box: Box, options: CaptureOptions): Promise<string> {
  const width = options.width ?? EXPORT_WIDTH;
  const height = options.height ?? EXPORT_HEIGHT;
  const fit = fitTransform(box, { width, height, maxScale: options.maxScale ?? 1 });

  return toPng(element, {
    backgroundColor: options.background,
    width,
    height,
    // `pixelRatio: 1` because the frame is already the output size; letting it
    // follow the screen would give a 3840px file on a retina display.
    pixelRatio: 1,
    style: {
      width: `${width}px`,
      height: `${height}px`,
      transform: `translate(${fit.x}px, ${fit.y}px) scale(${fit.scale})`,
      transformOrigin: '0 0',
    },
  });
}

export function downloadDataUrl(filename: string, dataUrl: string): void {
  const anchor = document.createElement('a');
  anchor.href = dataUrl;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

/** The box an element occupies in its own coordinates, including what scrolled out of view. */
export function elementBox(element: HTMLElement): Box {
  return { minX: 0, minY: 0, maxX: element.scrollWidth, maxY: element.scrollHeight };
}
